import {
  ATTENDANCE_CENTER_ROUTE_PREFIX,
  CASH_CONTROL_ROUTE_PREFIX,
  SUBMODULE_KEY_CENTRAL_ATENDIMENTO,
} from '@core/utilities/backend-menu.util';

/**
 * Allowlist of features that have been adapted for mobile (TASK-59145).
 *
 * The mobile menu drawer filters every `MenuItem` against this set before
 * rendering. An item is shown on mobile if and only if its backend `key` is
 * in `MOBILE_READY_FEATURE_KEYS` OR its `route` starts with one of the
 * `MOBILE_READY_ROUTE_PREFIXES` (used as a fallback when the backend hasn't
 * assigned a key yet). A parent whose children are all filtered out
 * collapses to nothing — empty folders never reach the user.
 *
 * Adding a feature to this list is the LAST commit of its mobile-adaptation
 * PR: nothing reaches the menu until the screen has been verified on a real
 * device and the manual viewport checklist passes.
 *
 * Removing a key is the fastest "kill switch" if a feature is found broken
 * on mobile in production — it hides the entry without redeploying any
 * feature module.
 *
 * ── One declaration, two lists ──────────────────────────────────────────────
 * `MOBILE_READY_FEATURE_KEYS` and `MOBILE_READY_ROUTE_PREFIXES` are DERIVED
 * from {@link MOBILE_READY_FEATURES} below. They used to be two hand-written
 * literals, and a feature that landed in one and not the other appeared in the
 * drawer (which matches by key) while vanishing from the home grid (which then
 * passed only a route). That shipped twice — Mensagens, TASK-59246, and
 * Estabelecimentos de Saúde — and no test could fail, because every test was a
 * literal example of a feature someone had remembered to add to both.
 *
 * With one declaration the halves cannot drift: `routePrefix` is REQUIRED, and
 * `null` is how a feature states it has no route of its own. Omitting it is a
 * compile error, not a silent half-entry.
 */
export interface MobileReadyFeature {
  /** What ships, for the reader — and the name a declaration problem is reported under. */
  readonly feature: string;
  /**
   * Every backend menu key the feature may arrive under. More than one when
   * the backend's key for it is not settled and any of them may ship.
   */
  readonly keys: readonly string[];
  /**
   * Route prefix its screens live under (must start with `/`), or `null` when
   * the feature has no route of its own — a folder such as `indicadores`
   * (route `null`, children only) legitimately has none. Required rather than
   * optional so that adding a feature forces an answer instead of producing an
   * entry in the key list alone.
   */
  readonly routePrefix: string | null;
}

/** The single source both exported allowlists are built from. */
export const MOBILE_READY_FEATURES: readonly MobileReadyFeature[] = Object.freeze([
  // Phase 1 ships with nothing in the allowlist beyond Início, the landing
  // page where there is nothing to break — feature pages are added one at a
  // time during Phase 6.
  { feature: 'Início', keys: ['inicio', 'home'], routePrefix: '/home' },
  // Phase 6.2 (TASK-59145) — Mensagens / Communication. Inbox table renders
  // as cards on phones, modais (compose, view, group management) become
  // fullscreen sheets via `modal-fullscreen-mobile`.
  { feature: 'Mensagens', keys: ['comunicacao'], routePrefix: '/messages' },
  // Phase 6.3 (TASK-59145) — Monitoramento de pontualidade. Header empilha,
  // filtros viram bottom sheet, tabs scroll horizontal, modais fullscreen.
  // Backend may emit any of these as the menu item key — list every known
  // candidate so the allowlist matches whichever ships. `indicadores` is the
  // FOLDER that holds them (no route of its own; it reaches the drawer
  // through its children).
  {
    feature: 'Monitoramento de pontualidade',
    keys: ['monitoramento_pontualidade', 'monitoramento_pontualidade_resultados', 'indicadores'],
    routePrefix: '/punctuality-monitoring',
  },
  // Phase 6.6 (TASK-59145) — Guide-flow dashboard (Auditoria de
  // pré-faturamento). Report and filters cards default to collapsed on
  // phones; multi-select dropdowns become bottom sheets; details/history/
  // movement modais ficam fullscreen.
  {
    feature: 'Auditoria de pré-faturamento',
    keys: ['guide-flow', 'auditoria_pre_faturamento'],
    routePrefix: '/guide-flow',
  },
  // Phase 6.6 follow-up (TASK-59145, customer ask) — Cadastro do fluxo
  // de auditoria. Stages table → card-stack, drag-and-drop hidden
  // (touch-unfriendly), up/down arrows still work, modal fullscreen.
  // The `desktopOnly: true` flag and MobileBlockedGuard were removed
  // from the route in `app-routing.module.ts`.
  {
    feature: 'Cadastro do fluxo de auditoria',
    keys: ['guide-flow-config', 'cadastro_fluxo_auditoria_pre_faturamento'],
    routePrefix: '/guide-flow-config',
  },
  // TASK-60341 — Cadastros → Estabelecimento de Saúde. Opens as an in-app
  // overlay (modal) whose shell is fullscreen on phones (`modal-fullscreen-
  // mobile`); the mobile drawer is the primary validation channel for it, so
  // the leaf must reach the drawer to be tappable.
  // NOTE: `/health-facilities` is an OVERLAY IDENTIFIER, not a router path.
  // The screen opens as an in-app modal (`opensAsOverlay('estabelecimento_
  // saude')` is true) and `backend-menu.util.spec.ts` pins that
  // `isSpaInternalRoute('/health-facilities')` is false. Do not delete this
  // prefix on the grounds that "no route maps to it" — that is by design.
  {
    feature: 'Estabelecimento de Saúde',
    keys: ['estabelecimento_saude'],
    routePrefix: '/health-facilities',
  },
  // TASK-60341/US #60630 — Cadastros → Filas de Coleta. Same pattern as
  // Estabelecimento de Saúde above: opens as an in-app overlay (modal) with
  // a fullscreen shell on phones, and the mobile drawer is the primary
  // validation channel for it, so the leaf must reach the drawer to be
  // tappable.
  //
  // Arrived from the trunk as a bare key in the old two-hand-written-lists
  // shape, and is declared here in the single-declaration shape instead — see
  // the header: a feature that lands in one list and not the other appears in
  // the drawer and vanishes from the home grid. `/collection-queues` is the
  // same kind of OVERLAY IDENTIFIER as `/health-facilities` above, not a
  // router path.
  {
    feature: 'Filas de Coleta',
    keys: ['cadastro_filas_coleta'],
    routePrefix: '/collection-queues',
  },
  // US #61464 — Integrações → Tasy. A routed placeholder page (title, subtitle
  // and one notice card); no table and no modal, so it needs no mixin from
  // `_mobile-mixins.scss` — only the `@include mobile` gutter its own SCSS
  // already carries.
  //
  // The key declared is the CHILD's. `integracoes` is the FOLDER, and
  // `MenuDrawerComponent.filterItem` keeps a folder only when one of its
  // children survives the filter — never on the folder's own key. Declaring it
  // would read as permission the mechanism never grants.
  {
    feature: 'Integrações — Tasy',
    keys: ['integracao_tasy'],
    routePrefix: '/integrations',
  },
  // US #61577 — Vacinas → Configuração. Same pattern as the two overlays above:
  // an in-app modal with a fullscreen shell on phones, opened by submodule key.
  // The mobile drawer is a primary validation channel for it (C-6.6), so the leaf
  // must reach the drawer to be tappable.
  // NOTE: `/vaccines-config` is an OVERLAY IDENTIFIER, not a router path — the
  // migration seeds it as the submodule's ROTA and `backend-menu.util.spec.ts`
  // pins that `isSpaInternalRoute('/vaccines-config')` is false. Do not delete
  // this prefix on the grounds that "no route maps to it"; the home quick-access
  // grid passes only a route, so without it the card vanishes from the home.
  {
    feature: 'Configuração de Vacinas',
    keys: ['vacinas_configuracao'],
    routePrefix: '/vaccines-config',
  },
  // US #61499 — Faturamento → Controle de Caixa. The widest grid of the
  // product (12 columns, 8 of them monetary, plus a header-less stripe)
  // degrades to the card list of `<app-responsive-table>` on phones, so the
  // leaf has to reach the drawer and the home grid to be tappable at all.
  // The prefix is the router path of the page (`/cash-control`), read from the constant that sits
  // beside the submodule key, so the pair the menu seed ships together cannot drift apart here.
  {
    feature: 'Controle de Caixa',
    keys: ['controle_caixa'],
    routePrefix: CASH_CONTROL_ROUTE_PREFIX,
  },
  // US #62981 — Recepção → Central de atendimento (search and patient record), added after the §7
  // viewport run passed (DEV-6). The menu seed ships `/central-atendimento`, which
  // `BACKEND_ROUTE_TO_ANGULAR` translates to this router path.
  {
    feature: 'Central de atendimento',
    keys: [SUBMODULE_KEY_CENTRAL_ATENDIMENTO],
    routePrefix: ATTENDANCE_CENTER_ROUTE_PREFIX,
  },
  // US #61003 — Cadastros → Setores do Laboratório. DELIBERATELY NOT LISTED YET, and this comment
  // is here so the gap reads as a decision rather than as the entry someone forgot: this list is
  // the only one of the leaf's eight registration points with no compile-time guard, so nothing
  // else would say so.
  //
  // The header above is the rule being followed — a feature joins this list as the LAST commit of
  // its mobile-adaptation PR, after the screen has been verified on a real device and the manual
  // viewport checklist of `docs/MOBILE-RESPONSIVO.md` §7 passes. That run has not happened for this
  // screen, so the entry cannot land yet, even though the delivery already ships the mobile
  // treatment (`tap-target-mobile` at four sites, `<app-responsive-table>` card list).
  //
  // WHAT UNBLOCKS IT, and the order it has to happen in: the leaf opens as an overlay, so the
  // drawer and the home grid — both gated by `isMobileReadyMenuItem` — are the only two ways in,
  // and neither offers it while this entry is absent. The viewport run therefore has to be done
  // against a build with the entry added locally (or with the guard bypassed); once it passes, the
  // entry lands here, beside the two Cadastros siblings above, in the same shape:
  //   { feature: 'Setores do Laboratório', keys: ['cadastro_setores_laboratorio'],
  //     routePrefix: '/laboratory-sectors' }
  // `/laboratory-sectors` is an OVERLAY IDENTIFIER of the same kind as the three prefixes above,
  // not a router path.
  //
  // US #62189 — Cadastros → Tabela de Preços. DELIBERATELY NOT LISTED YET, for the same reason as
  // Setores do Laboratório right above: the entry is the LAST commit of the mobile adaptation, after
  // the manual viewport checklist of `docs/MOBILE-RESPONSIVO.md` §7 passes on a real device, and that
  // run has not happened for this screen. The leaf also opens as an overlay, so the same order
  // applies: run the checklist against a build with the entry added locally, then land it here:
  //   { feature: 'Tabela de Preços', keys: [SUBMODULE_KEY_CADASTRO_TABELA_PRECO],
  //     routePrefix: '/price-tables' }
  // `/price-tables` is the OVERLAY IDENTIFIER the menu seed ships as the submodule's ROTA, not a
  // router path. Until then the drawer gate of the layout (`withoutGatedLeaves`) has nothing to hide
  // on mobile; it is already in place for when the entry lands.
  //
  // US #62593 — Faturamento → Faturamento SUS. DELIBERATELY NOT LISTED YET, for the same reason as
  // Setores do Laboratório right above: the header rule makes the entry the LAST commit of the mobile
  // adaptation, after the manual viewport checklist of `docs/MOBILE-RESPONSIVO.md` §7 passes on a real
  // device, and that run has not happened. The page already ships the mobile treatment (the gutter and the
  // one-column form under `@include mobile`, the shared fields and buttons). Once the run passes, the entry
  // lands here in this shape, importing the constant beside `CASH_CONTROL_ROUTE_PREFIX`:
  //   { feature: 'Faturamento SUS', keys: ['faturamento_sus'], routePrefix: SUS_BILLING_ROUTE_PREFIX }
  // `/sus-billing` is the router path of the page, like `/cash-control`.
]);

/**
 * Structural problems in a feature declaration, one message per problem, in
 * declaration order. Empty means the declaration is sound.
 *
 * Takes the list as an argument rather than reading the module constant so the
 * rules themselves are testable against a deliberately broken declaration —
 * a guard that can only ever be run against known-good data proves nothing.
 */
export function findMobileReadyDeclarationProblems(
  features: readonly MobileReadyFeature[],
): string[] {
  const problems: string[] = [];
  for (const { feature, keys, routePrefix } of features) {
    if (keys.length === 0) {
      problems.push(`${feature}: declares no feature key`);
    }
    if (routePrefix !== null && !routePrefix.startsWith('/')) {
      problems.push(`${feature}: route prefix '${routePrefix}' must start with '/'`);
    }
  }
  return problems;
}

/**
 * Backend menu keys of every mobile-ready feature. Derived — do not hand-edit;
 * add to {@link MOBILE_READY_FEATURES}.
 */
export const MOBILE_READY_FEATURE_KEYS: ReadonlySet<string> = new Set<string>(
  MOBILE_READY_FEATURES.flatMap(feature => feature.keys),
);

/**
 * Route-prefix fallback used when a `MenuItem` has no `key`. Prefer matching by
 * key whenever one is available — routes are less stable (locale variants,
 * redirects), keys are language-agnostic. Derived — do not hand-edit; add to
 * {@link MOBILE_READY_FEATURES}.
 *
 * Each entry starts with `/` (enforced by
 * {@link findMobileReadyDeclarationProblems}). Matching is case-insensitive
 * prefix, on whole segments.
 */
export const MOBILE_READY_ROUTE_PREFIXES: readonly string[] = Object.freeze(
  MOBILE_READY_FEATURES
    .map(feature => feature.routePrefix)
    .filter((prefix): prefix is string => prefix !== null),
);

/**
 * Returns true when the given menu item should be visible in the mobile
 * drawer. Used by the drawer renderer and by the leaf-filtering pass that
 * collapses empty parents.
 */
export function isMobileReadyMenuItem(item: { key?: string; route?: string }): boolean {
  if (item.key && MOBILE_READY_FEATURE_KEYS.has(item.key)) return true;
  if (!item.route) return false;
  const route = item.route.toLowerCase();
  return MOBILE_READY_ROUTE_PREFIXES.some(prefix => route === prefix || route.startsWith(prefix + '/'));
}
